import React, { useEffect, useRef, useState } from "react";
import VerificationBadge from "../assets/svg/verification-badge.svg";
import { TextInput } from "../components/common/ui/Inputs";
import otpSchema from "../validation/otpSchema";
import { showToastNotification } from "../components/common/ui/NotificationModal/ToastNotication";
import { useDispatch, useSelector } from "react-redux";
import { userRegisterAction } from "../_store";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

function OTP() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const OTPRef = useRef(null);
  const email =
    location?.state?.email || sessionStorage.getItem("bpkLoginOTPEmail");
  const requestFrom = location?.pathname?.split("/")[1];
  const [errorMessage, setErrorMessage] = useState({});
  const [submit, setSubmit] = useState(false);
  const [verifyOTP, setVerifyOTP] = useState({
    email: email,
    otp: "",
  });

  useEffect(() => {
    if (location?.state?.email) {
      sessionStorage.setItem("bpkLoginOTPEmail", location.state.email);
    }
  }, [location?.state?.email]);
  const OTP_DURATION = 15 * 60 * 1000; // 15 mins
  const getRemainingTime = () => {
    const expiry = sessionStorage.getItem("bpkLoginOTPExpiry");

    if (!expiry) return 0;

    const diff = Math.floor((Number(expiry) - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  };

  const [otpTimer, setOtpTimer] = useState(getRemainingTime);

  const startOtpTimer = () => {
    const expiryTime = Date.now() + OTP_DURATION;
    sessionStorage.setItem("bpkLoginOTPExpiry", expiryTime.toString());
    setOtpTimer(getRemainingTime());
  };

  const { resendOTPResponse } = useSelector((x) => x?.register);

  useEffect(() => {
    OTPRef.current?.focus();
  }, []);

  // Initialize timer only once
  useEffect(() => {
    //OTP Timer
    if (requestFrom === "register" || !email) return;

    const existingExpiry = sessionStorage.getItem("bpkLoginOTPExpiry");
    if (!existingExpiry) {
      startOtpTimer();
    } else {
      setOtpTimer(getRemainingTime());
    }
  }, [email, requestFrom]);

  // Timer interval
  useEffect(() => {
    if (requestFrom === "register" || !email || otpTimer <= 0) return;

    const interval = setInterval(() => {
      const remaining = getRemainingTime();
      setOtpTimer(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        if (email) {
          dispatch(userRegisterAction?.clearOTP({ email }));
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [dispatch, email, requestFrom, otpTimer > 0]);

  const submitVerifyOTP = (e) => {
    e.preventDefault();
    setErrorMessage({});
    setSubmit(true);

    const { error } = otpSchema(verifyOTP);

    if (error?.details) {
      setSubmit(false);

      error.details.forEach((item) => {
        const { path } = item;
        setErrorMessage((prev) => ({
          ...prev,
          [path[0]]: item.message,
        }));
      });

      return;
    }

    const request =
      requestFrom === "login"
        ? dispatch(userRegisterAction?.verifyLoginOTP(verifyOTP))
        : dispatch(userRegisterAction?.verifyOTP(verifyOTP));

    request
      .then((result) => {
        setSubmit(false);

        if (result?.payload?.status === 1) {
          if (requestFrom === "register") {
            showToastNotification(
              "One-Time Code Verified Successfully",
              "success",
            );
          }

          dispatch(userRegisterAction?.clearOTPResponse());

          sessionStorage.removeItem("bpkLoginOTPExpiry");
          sessionStorage.removeItem("bpkLoginOTPEmail");
          setOtpTimer(0);

          requestFrom === "login" ? navigate("/Home") : navigate("/login");
        } else {
          showToastNotification(result?.error?.message, "error");
        }
      })
      .catch(() => {
        showToastNotification("Failed to verify OTP", "error");
        setSubmit(false);
      });
  };

  const handleOTPChange = (e) => {
    const { value, name } = e.target;

    if (value.startsWith(" ")) return;

    const regex = /^[a-zA-Z0-9\s]*$/;
    if (!regex.test(value)) return;

    setVerifyOTP((prev) => ({
      ...prev,
      [name]: value.toUpperCase(),
    }));
  };

  const resendLoginOTP = () => {
    if (requestFrom === "register") return;

    setSubmit(true);

    dispatch(userRegisterAction?.resendLoginOTP({ email }))
      .then((result) => {
        setSubmit(false);

        if (result?.payload?.status === 1) {
          showToastNotification(result?.payload?.message, "success");
          startOtpTimer();
        } else {
          showToastNotification(result?.error?.message, "error");
        }
      })
      .catch(() => {
        showToastNotification("Failed to Resend One-Time Code", "error");
        setSubmit(false);
      });
  };

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;

    return `${min.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };

  if (!email) {
    return (
      <Navigate
        to={requestFrom === "register" ? "/register" : "/login"}
        replace
      />
    );
  }

  return (
    <section className="otp-section">
      <div className="otp-card">
        <img
          height="92px"
          width="92px"
          src={VerificationBadge}
          alt="verification-badge"
        />

        <h1 className="otp-label text-center">Check your inbox</h1>

        <h1 className="otp-verify-label text-center">
          Enter the verification code we just sent to{" "}
          <span className="otp-email-text">{email ? `${email}.` : "***"}</span>
        </h1>

        <form
          className="d-flex flex-column gap-3 align-items-center"
          onSubmit={submitVerifyOTP}
        >
          <div>
            <TextInput
              name="otp"
              label="Enter One-Time code"
              placeholder="Enter the One-Time code"
              maxLength={6}
              value={verifyOTP?.otp}
              handleChange={handleOTPChange}
              required
              errorMessage={errorMessage}
              inputRef={OTPRef}
              cName="otp-input-field"
            />

            {requestFrom !== "register" && (
              <div className="login-otp-session-timer">
                Time Remaining : <span>{formatTime(otpTimer)}</span>
              </div>
            )}
          </div>

          <div className="div-center">
            <button
              type="submit"
              className="sign-in-button"
              disabled={
                submit || (requestFrom !== "register" && otpTimer === 0)
              }
            >
              Verify
              {!submit ? (
                <i className="css-arrow-icon bi bi-arrow-right"></i>
              ) : (
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              )}
            </button>
          </div>
        </form>

        {requestFrom === "login" && (
          <p className="resend-link">
            Not Received Your Code?{" "}
            <button
              className="resend-email-btn"
              onClick={() => {
                resendLoginOTP();
              }}
              disabled={submit || otpTimer !== 0}
            >
              Resend Email
            </button>
          </p>
        )}
      </div>
    </section>
  );
}

export default OTP;
